import React, { useState } from 'react';
import { Image as ImageIcon, X, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function TestimonialsBox({ name, description, translatedDescription, image }) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);

  // Check if translation is available and different from description
  const hasTranslation = !!translatedDescription && translatedDescription !== description;

  return (
    <div className="w-full bg-white border border-gray-100 rounded-2xl shadow-md p-6 sm:p-8 flex flex-col md:flex-row gap-6 items-start transition-all duration-300 hover:shadow-xl hover:border-gray-200">
      {/* Testimonial Content */}
      <div className="flex-grow w-full">
        
        {/* Name & Age */}
        <h4 className="text-base font-bold text-[#162E3B] mb-3">
          {name}
        </h4>
        
        {/* Quote Text */}
        <div className="relative mb-6">
          <span className="absolute -top-4 -left-2 text-5xl text-[#FFD82D]/40 font-serif pointer-events-none">“</span>
          <p 
            className="text-gray-700 text-base leading-relaxed italic pl-4 border-l-2 border-[#FFD82D]/40 whitespace-pre-line"
          >
            {isTranslated && hasTranslation ? translatedDescription : description}
          </p>
        </div>

        {/* Buttons / Controls */}
        <div className="flex flex-wrap gap-3">
          {hasTranslation && (
            <button 
              onClick={() => setIsTranslated(!isTranslated)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-[#FFD82D] text-[#162E3B] font-extrabold rounded-xl border border-gray-200 text-sm transition-all active:scale-98 focus:outline-none focus:ring-4 focus:ring-[#FFD82D]"
            >
              <Languages className="w-5 h-5" />
              {isTranslated ? t('home.testimonials.showOriginal') : t('home.testimonials.translate')}
            </button>
          )}

          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#FFD82D] text-[#162E3B] hover:bg-[#162E3B] hover:text-white font-extrabold rounded-xl border-2 border-transparent text-sm transition-all active:scale-98 focus:outline-none focus:ring-4 focus:ring-[#FFD82D]"
          >
            <ImageIcon className="w-5 h-5" />
            {t('home.testimonials.viewPhoto')}
          </button>
        </div>
      </div>

      {/* Full-Screen Lightbox Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setIsModalOpen(false)} 
              className="absolute top-4 right-4 bg-black/60 text-white hover:bg-black p-2.5 rounded-full transition shadow-md z-10 focus:outline-none focus:ring-4 focus:ring-[#FFD82D]"
              aria-label={t('home.testimonials.closePhoto')}
            >
              <X className="w-6 h-6" />
            </button>

            {/* Modal Image container */}
            <div className="flex items-center justify-center w-full h-[65vh] rounded-lg overflow-hidden bg-gray-150">
              <img
                className="max-w-full max-h-full object-contain"
                src={image}
                alt={`${name} portrait`}
                loading="lazy"
              />
            </div>
            
            {/* Legend inside Modal */}
            <div className="mt-3 text-center">
              <p className="text-base font-bold text-[#162E3B]">{name}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestimonialsBox;
